import { useState, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import type { QuestionnaireOut, StudyQuestionnaire } from "../../api/types";
import { evaluatePresentWhen } from "../../features/dashboard/protocol";

const SCHEMA_VERSION = "v1";

// Generic renderer for protocol-defined study questionnaires: walks the spec's
// fields (kind → widget), evaluates present_when against the current answers,
// and submits the payload as a `study` questionnaire under the spec's key.
// The built-in one-time/daily forms stay bespoke — this renders only the
// admin-configured supplemental instruments.
export function QuestionnaireForm({
  spec,
  onSubmitted,
}: {
  spec: StudyQuestionnaire;
  onSubmitted: () => Promise<void>;
}): ReactElement {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setAnswer(key: string, value: string): void {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  const visible = spec.fields.filter((f) => evaluatePresentWhen(f.present_when, answers));

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    const payload: Record<string, unknown> = {};
    for (const f of visible) {
      const raw = answers[f.key];
      if (raw === undefined || raw === "") continue;
      payload[f.key] = f.kind === "integer" || f.kind === "number" ? Number(raw) : raw;
    }
    try {
      await api<QuestionnaireOut>("POST", "/participants/me/questionnaire", {
        json: {
          questionnaire_type: "study",
          questionnaire_key: spec.key,
          payload,
          schema_version: SCHEMA_VERSION,
        },
      });
      setAnswers({});
      await onSubmitted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit the questionnaire.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="consent-block">
      <h3>{spec.title}</h3>
      {visible.map((f) =>
        f.kind === "yesno" ? (
          // Radios carry their own labels — no outer <label> wrapper (nested
          // labels break the radios' accessible names).
          <fieldset key={f.key} className="field">
            <legend>{f.label}</legend>
            <div className="choice-row">
              {(["yes", "no"] as const).map((v) => (
                <label key={v} className={`choice${answers[f.key] === v ? " selected" : ""}`}>
                  <input
                    type="radio"
                    name={`${spec.key}_${f.key}`}
                    checked={answers[f.key] === v}
                    onChange={() => setAnswer(f.key, v)}
                  />
                  {v === "yes" ? "Yes" : "No"}
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
        <label key={f.key} className="field">
          <span>{f.label}</span>
          {f.kind === "coded" ? (
            <select value={answers[f.key] ?? ""} onChange={(e) => setAnswer(f.key, e.target.value)}>
              <option value="">Select…</option>
              {Object.entries(f.choices ?? {}).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          ) : f.kind === "integer" || f.kind === "number" ? (
            <input
              type="number"
              step={f.kind === "integer" ? 1 : "any"}
              value={answers[f.key] ?? ""}
              onChange={(e) => setAnswer(f.key, e.target.value)}
            />
          ) : (
            <input
              type="text"
              value={answers[f.key] ?? ""}
              onChange={(e) => setAnswer(f.key, e.target.value)}
            />
          )}
        </label>
        ),
      )}
      {error ? <p className="form-error">{error}</p> : null}
      <button className="btn-primary-glow" onClick={() => void submit()} disabled={busy}>
        {busy ? "Submitting…" : "Submit"}
      </button>
    </div>
  );
}
